import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppShell } from '@/components/layout/AppShell';
import { PageContainer } from '@/components/layout/PageContainer';
import { TopBar } from '@/components/layout/TopBar';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { useCreateSupportTicket } from '@/api/queries/support';
import { useUIStore } from '@/store/ui';
import { paths } from '@/routes/paths';
import { isAPIError } from '@/api/error';

export function SupportNewPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pushToast = useUIStore((s) => s.pushToast);
  const create = useCreateSupportTicket();

  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  const canSubmit = subject.trim().length >= 3 && body.trim().length >= 10;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    try {
      const tk = await create.mutateAsync({
        subject: subject.trim(),
        body: body.trim(),
      });
      pushToast({ body: t('support.created'), variant: 'success' });
      navigate(paths.supportTicket(tk.id), { replace: true });
    } catch (err) {
      const key = isAPIError(err) ? err.i18nKey : 'errors.generic';
      pushToast({ body: t(key, { defaultValue: t('errors.generic') }), variant: 'error' });
    }
  };

  return (
    <AppShell>
      <PageContainer paddingX="md">
        <TopBar back title={t('support.new_ticket')} />
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Input
            label={t('support.subject')}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            maxLength={120}
            required
          />
          <label
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
              fontSize: 13,
              color: 'var(--color-text-on-dark-muted)',
            }}
          >
            {t('support.message')}
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={8}
              maxLength={4000}
              required
              style={{
                resize: 'vertical',
                padding: 12,
                borderRadius: 12,
                border: '1px solid var(--color-border, rgba(255,255,255,0.12))',
                background: 'var(--color-bg-elevated, rgba(255,255,255,0.04))',
                color: 'inherit',
                font: 'inherit',
                minHeight: 140,
              }}
            />
          </label>

          <Button
            type="submit"
            block
            size="lg"
            variant="inverted"
            disabled={!canSubmit}
            loading={create.isPending}
          >
            {t('common.submit')}
          </Button>
        </form>
      </PageContainer>
    </AppShell>
  );
}
